import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import dicomParser from 'dicom-parser';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
/** Fetch a DICOM file and parse it into an ImageData for canvas rendering. */
async function fetchDicomImageData(src) {
    const imageUrl = src.startsWith('/') ? window.location.origin + src : src;
    const response = await fetch(imageUrl);
    if (!response.ok)
        throw new Error(`Failed to fetch: ${response.status}`);
    const arrayBuffer = await response.arrayBuffer();
    const byteArray = new Uint8Array(arrayBuffer);
    const dataSet = dicomParser.parseDicom(byteArray);
    const width = dataSet.uint16('x00280011') || 512;
    const height = dataSet.uint16('x00280010') || 512;
    const bitsAllocated = dataSet.uint16('x00280100') || 16;
    const bitsStored = dataSet.uint16('x00280101') || 12;
    const pixelRepresentation = dataSet.uint16('x00280103') || 0;
    const samplesPerPixel = dataSet.uint16('x00280002') || 1;
    const photometricInterpretation = dataSet.string('x00280004') || 'MONOCHROME2';
    let windowCenter = dataSet.floatString('x00281050');
    let windowWidth = dataSet.floatString('x00281051');
    const rescaleSlope = dataSet.floatString('x00281053') || 1;
    const rescaleIntercept = dataSet.floatString('x00281052') || 0;
    const pixelDataElement = dataSet.elements.x7fe00010;
    if (!pixelDataElement)
        throw new Error('No pixel data found');
    const imageData = new ImageData(width, height);
    const pixelDataOffset = pixelDataElement.dataOffset;
    const pixelDataLength = pixelDataElement.length;
    let minPixel = Infinity;
    let maxPixel = -Infinity;
    const storedRange = 2 ** bitsStored;
    const storedMin = pixelRepresentation === 1 ? -(storedRange / 2) : 0;
    const storedMax = pixelRepresentation === 1 ? storedRange / 2 - 1 : storedRange - 1;
    const storedMask = storedRange - 1;
    const normalizeStoredValue = (raw) => {
        const masked = raw & storedMask;
        if (pixelRepresentation === 1) {
            const signBit = 1 << (bitsStored - 1);
            return masked & signBit ? masked - storedRange : masked;
        }
        return masked;
    };
    const pixelValues = [];
    if (bitsAllocated === 16) {
        const pixelData = new DataView(arrayBuffer, pixelDataOffset, pixelDataLength);
        const numPixels = width * height * samplesPerPixel;
        for (let i = 0; i < numPixels && i * 2 < pixelDataLength; i++) {
            let value;
            if (pixelRepresentation === 1) {
                value = pixelData.getInt16(i * 2, true);
            }
            else {
                value = pixelData.getUint16(i * 2, true);
            }
            value = normalizeStoredValue(value);
            value = Math.min(Math.max(value, storedMin), storedMax);
            value = value * rescaleSlope + rescaleIntercept;
            pixelValues.push(value);
            if (value < minPixel)
                minPixel = value;
            if (value > maxPixel)
                maxPixel = value;
        }
    }
    else if (bitsAllocated === 8) {
        const pixelData = new DataView(arrayBuffer, pixelDataOffset, pixelDataLength);
        for (let i = 0; i < pixelDataLength; i++) {
            const rawValue = pixelRepresentation === 1 ? pixelData.getInt8(i) : pixelData.getUint8(i);
            let value = normalizeStoredValue(rawValue);
            value = Math.min(Math.max(value, storedMin), storedMax);
            value = value * rescaleSlope + rescaleIntercept;
            pixelValues.push(value);
            if (value < minPixel)
                minPixel = value;
            if (value > maxPixel)
                maxPixel = value;
        }
    }
    if (!windowWidth || !windowCenter) {
        windowWidth = maxPixel - minPixel;
        windowCenter = (maxPixel + minPixel) / 2;
    }
    const wMin = windowCenter - windowWidth / 2;
    const wMax = windowCenter + windowWidth / 2;
    const isInverted = photometricInterpretation === 'MONOCHROME1';
    for (let i = 0; i < pixelValues.length; i++) {
        let value = pixelValues[i];
        if (value <= wMin) {
            value = 0;
        }
        else if (value >= wMax) {
            value = 255;
        }
        else {
            value = ((value - wMin) / windowWidth) * 255;
        }
        if (isInverted)
            value = 255 - value;
        const idx = i * 4;
        imageData.data[idx] = value;
        imageData.data[idx + 1] = value;
        imageData.data[idx + 2] = value;
        imageData.data[idx + 3] = 255;
    }
    return imageData;
}
export function DicomThumbnail({ src, alt, className }) {
    const { t } = useTranslation();
    const canvasRef = useRef(null);
    const { data: imageData, isError: error } = useQuery({
        queryKey: ['dicom-thumbnail', src],
        queryFn: () => fetchDicomImageData(src),
        staleTime: Infinity,
        gcTime: 10 * 60_000,
    });
    const isLoaded = !!imageData;
    // Render ImageData to canvas when available
    useEffect(() => {
        if (!imageData || !canvasRef.current)
            return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        canvas.width = imageData.width;
        canvas.height = imageData.height;
        ctx.putImageData(imageData, 0, 0);
    }, [imageData]);
    return (_jsxs("div", { className: cn('relative flex items-center justify-center overflow-hidden bg-black', className), title: alt || undefined, children: [!isLoaded && !error && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: _jsx("div", { className: "h-5 w-5 animate-spin rounded-full border-2 border-gray-600 border-t-gray-300" }) })), error && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center p-1 text-center text-[10px] text-gray-500", children: _jsx("span", { children: t('dicom.noPreview') }) })), _jsx("canvas", { ref: canvasRef, className: cn('max-h-full max-w-full object-contain', isLoaded ? 'opacity-100' : 'opacity-0'), style: { imageRendering: 'auto' } })] }));
}
